import React, { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { AUTH, initialState, userReducer } from "./userReducer";
import { getToken, getUser, login, logout, refresh } from "./utils/auth-client";
import { getTokenExpiration } from "./utils/api-client-jwt";
import { AuthContext } from "../contexts/authContext";
import { CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";

/**
 * Провайдер аутентификации, управляющий состоянием пользователя и токенами
 * @component
 * @param {Object} props - Свойства компонента
 * @param {React.ReactNode} props.children - Дочерние компоненты
 * @returns {JSX.Element} Компонент провайдера аутентификации
 */
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, initialState);
  const refreshTimerRef = useRef(null);
  const navigate = useNavigate();

  const isBasicAuth = process.env.REACT_APP_AUTH_METHOD.toLowerCase() === "basic";

  /**
   * Обрабатывает процесс входа пользователя
   * @async
   * @param {Object} credentials - Учетные данные пользователя
   * @param {string} credentials.username - Имя пользователя
   * @param {string} credentials.password - Пароль
   * @throws {Error} Ошибка аутентификации
   */
  const handleLogin = async (credentials) => {
    try {
      dispatch({ type: AUTH.PENDING });
      await login(credentials);
      const userData = await getUser();
      dispatch({
        type: AUTH.LOGIN,
        payload: {
          user: userData,
          authDate: new Date(),
        },
      });

      if (!isBasicAuth) {
        scheduleTokenRefresh();
      }

    } catch (error) {
      dispatch({ type: AUTH.REJECT, payload: { error } });
      throw error;
    }
  };

  /**
   * Обрабатывает процесс выхода пользователя
   * @async
   */
  const handleLogout = async () => {
    try {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      await logout();
      dispatch({ type: AUTH.LOGOUT });
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  /**
   * Планирует автоматическое обновление токена
   * @callback
   * @async
   */
  const scheduleTokenRefresh = useCallback(async () => {
    if (isBasicAuth) return;

    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    const token = getToken();
    if (!token) return;

    try {
      const expTime = getTokenExpiration(token);
      const currentTime = Date.now();
      const timeUntilExpiry = expTime - currentTime;

      if (timeUntilExpiry <= 60000) {
        console.log("Token expired or expiring soon, refreshing immediately");
        await refresh();
        scheduleTokenRefresh();
        return;
      }

      // Если токен уже истёк или истекает менее чем через минуту
      const refreshTime = Math.max(timeUntilExpiry * 0.95, timeUntilExpiry - 60000);

      console.log(`Token will be refreshed in ${Math.round(refreshTime / 1000)} seconds`);

      refreshTimerRef.current = setTimeout(async () => {
        try {
          await refresh();
          console.log("Token refreshed silently");
          scheduleTokenRefresh();
        } catch (error) {
          console.error("Scheduled refresh failed:", error);
          handleLogout();
        }
      }, refreshTime);
    } catch (error) {
      console.error("Error scheduling token refresh:", error);
    }

  }, [isBasicAuth]);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        dispatch({ type: AUTH.PENDING });
        const token = getToken();

        if (token) {

          if (isBasicAuth) {
            try {
              const userData = await getUser();
              dispatch({
                type: AUTH.LOGIN,
                payload: {
                  user: userData,
                  authDate: new Date(),
                },
              });
            } catch (error) {
              console.error("Basic auth check failed:", error);
              await logout();
              dispatch({ type: AUTH.LOGOUT });
            }
          } else {
            const expTime = getTokenExpiration(token);
            const isTokenValid = expTime > Date.now();

            if (isTokenValid) {
              const userData = await getUser();
              dispatch({
                type: AUTH.LOGIN,
                payload: {
                  user: userData,
                  authDate: new Date(),
                },
              });
              scheduleTokenRefresh();
            } else {
              try {
                await refresh();
                const userData = await getUser();
                dispatch({
                  type: AUTH.LOGIN,
                  payload: {
                    user: userData,
                    authDate: new Date(),
                  },
                });
                scheduleTokenRefresh();
              } catch (error) {
                console.error("Token refresh failed:", error);
                await logout();
                dispatch({ type: AUTH.LOGOUT });
              }
            }
          }
        } else {
          dispatch({ type: AUTH.LOGOUT });
        }
      } catch (error) {
        console.error("Auth check error:", error);
        dispatch({ type: AUTH.REJECT, payload: { error } });
      }
    };

    checkAuth();

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [scheduleTokenRefresh, isBasicAuth]);

  if (state.status === AUTH.PENDING) {
    return <CircularProgress />;
  }

  /**
   * Обновляет данные текущего пользователя
   * @param {Object} newUserData - Новые данные пользователя
   */
  const updateCurrentUser = (newUserData) => {
    dispatch({
      type: AUTH.LOGIN,
      payload: {
        user: {
          ...state?.user,
          ...newUserData
        },
        authDate: new Date(),
      },
    });
  };

  /**
   * Проверяет, имеет ли пользователь указанную роль
   * @param {string} requiredRole - Требуемая роль
   * @returns {boolean} true если пользователь имеет требуемую роль
   */
  const hasRole = (requiredRole) =>
    state?.user?.role?.name?.trim().toLowerCase() === requiredRole;

  return (
    <AuthContext.Provider
      value={{
        state, 
        dispatch,
        currentUser: state?.user,
        hasRole,
        isLoading: state.status === AUTH.PENDING,
        updateCurrentUser,
        handleLogin,
        handleLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
